<template>
<ProgressCircular v-if="loading"/>
  <form v-else novalidate :disabled="disabled" @submit.prevent="() => {}">
    <v-row no-gutters justify="space-between">
      <v-col cols="8">
        <v-card max-width="1200px" flat color="transparent">
          <v-card-title>
            <FormHeader
              :loading="loading"
              :link="'page-blog-posts'"
              :title="getTitle ? `${getTitle} пост блога` : `Создать пост блога`"
            />
          </v-card-title>
          <v-tabs v-model="activeTab">
            <v-tab v-for="tab in $options.tabs" :key="tab.value">
              {{ tab.title }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <PageContent>
                <v-card-text>
                  <v-container>
                    <InputTitleSlug
                      :is-loading="loading || disabled"
                      :title="fields.title"
                      :title-error="validationErrors.title"
                      :slug="fields.slug"
                      :slug-error="validationErrors.slug"
                      :isSlugEnabled="!isNew"
                      @change:title="fields.title = $event"
                      @change:slug="fields.slug = $event"
                      @input:slug="$v.fields.slug.$touch"
                    />
                    <v-row>
                      <v-col cols="12">
                        <span>Анонс (<TranslationIcon />)</span>
                        <v-textarea
                          v-model="fields.announcement"
                          outlined
                          :disabled="disabled"
                          :loading="loading"
                        />
                      </v-col>
                      <v-col cols="12">
                        <MediaFilePicker
                          :label="'Обложка'"
                          :multiple="false"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.coverImageId"
                          @change="fields.coverImageId = $event"
                        />
                      </v-col>
                      <v-col cols="12">
                        <MediaFilePicker
                          :label="'Миниатюра'"
                          :multiple="false"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.thumbnailId"
                          @change="fields.thumbnailId = $event"
                        />
                      </v-col>

                      <v-col cols="12">
                        <SeoSettings
                          :data="data"
                          :loading="loading || disabled"
                          @on-input-seo="fields = Object.assign({}, fields, $event)"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </PageContent>
            </v-tab-item>
            <v-tab-item>
              <PageContent>
                <v-card-text>
                  <DynamicListWrapper
                    :list="fields.widgets"
                    :error-messages="mixValidationErrors"
                    :available-items="$options.availableWidgets"
                    :copy-clipboard="mixCopyListItemClipboard"
                    :options="$options.options"
                    @list-sort="mixListItemsReassort"
                    @add="mixCreateListItemHandler"
                    @option="mixListItemOptionHandler"
                  >
                    <template #item="{ data }">
                      <component
                        :is="data.item.type"
                        :list-item="data.item"
                        :error-messages="mixDynamicListValidationErrors[data.itemIndex]"
                        @change="mixListItemChangeHandler($event, data.itemIndex)"
                        @change-item="mixListItemChangeItemHandler($event, data.itemIndex)"
                        @remove-item="mixListItemRemoveItemHandler($event, data.itemIndex)"
                      />
                    </template>
                  </DynamicListWrapper>
                </v-card-text>
              </PageContent>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          isShowSortOrder
          v-if="isNew"
          ref="manageSidePanel"
          :is-new="isNew"
          :is-loading="loading"
          :is-submit-disabled="disabled"
          :language="language"
          :translations="fields.translations"
          :sortOrder="fields.sortOrder"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @cancel="$router.push({ name: 'page-blog-posts' })"
          @update-sort-order="fields.sortOrder = Number($event)"
          @submit="submit"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              title="Статус:"
              :disabled="disabled"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
            <v-row align="center" no-gutters>
              <v-col cols="7">
                <span class="mr-5">Рекомендовано</span>
              </v-col>
              <v-col>
                <v-switch
                  v-model="fields.isRecommended"
                  inset
                  dense
                  :disabled="disabled"
                  :loading="loading"
                />
              </v-col>
            </v-row>
            <v-row align="center" no-gutters>
              <v-col cols="7">
                <span class="mr-5">Избранное</span>
              </v-col>
              <v-col>
                <v-switch
                  v-model="fields.isFeatured"
                  inset
                  dense
                  :disabled="disabled"
                  :loading="loading"
                />
              </v-col>
            </v-row>
          </template>
          <template #body>
            <v-card flat>
              <v-card-text v-if="!!blogCategories">
                <v-card-subtitle class="grey--text">Категория блога</v-card-subtitle>
                <v-select
                  v-model="fields.blogCategoryId"
                  outlined
                  value="4"
                  item-value="id"
                  item-text="title"
                  clearable
                  :items="blogCategories"
                  :disabled="disabled"
                  :loading="loading"
                  :error-messages="validationErrors.blogCategoryId"
                />
              </v-card-text>
            </v-card>
          </template>
        </ManageSidePanel>
        <ManageSidePanel
          v-else-if="data"
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          :is-loading="loading"
          :is-submit-disabled="disabled"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'blogPost'"
          :sortOrder="fields.sortOrder"
          @delete="$emit('delete-blog-post')"
          @cancel="$router.push({ name: 'page-blog-posts' })"
          @submit="submit"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @update-sort-order="fields.sortOrder = Number($event)"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              class-col="pt-6"
              title="Статус:"
              :disabled="disabled"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
            <v-row v-if="!isNew" align="center">
              <v-col cols="6">
                <span>Посмотреть на сайте</span>
              </v-col>
              <v-col cols="6">
                <v-btn
                  icon
                  link
                  color="primary"
                  target="_blank"
                  :href="redirerectToWebPortal"
                  :loading="loading"
                  :disabled="disabled"
                  ><v-icon>mdi-web</v-icon></v-btn
                >
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6">
                <span>Рекомендовано</span>
              </v-col>
              <v-col>
                <v-switch
                  v-model="fields.isRecommended"
                  inset
                  dense
                  :disabled="disabled"
                  :loading="loading"
                />
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6">
                <span>Избранное</span>
              </v-col>
              <v-col cols="6">
                <v-switch
                  v-model="fields.isFeatured"
                  inset
                  dense
                  :disabled="disabled"
                  :loading="loading"
                />
              </v-col>
            </v-row>
          </template>
          <template #body>
            <v-card flat>
              <v-card-text>
                <v-card-subtitle class="grey--text">Категория блога</v-card-subtitle>
                <v-select
                  v-model="fields.blogCategoryId"
                  outlined
                  item-value="id"
                  item-text="title"
                  clearable
                  :items="blogCategories"
                  :disabled="disabled"
                  :loading="loading"
                  :error-messages="validationErrors.blogCategoryId"
                />
              </v-card-text>
            </v-card>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>
<script>
import widgetParentMix from '@/mixins/widgets/widgetParent.mix.js'
import parentDynamicList from '@/mixins/dynamic-list/parentDynamicList.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'

import tabs from '@/configs/tabs.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'
import validationRules from './validation-rules.js'
import { commonWidgetList } from '@/utils/widgets'
import { mixForm } from '@/utils/form.mix.js'
import Widget from '@/utils/widgets/Widget'

import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import MediaFilePicker from '@/components/media-file-picker'
import SeoSettings from '@/components/seo-settings'
import DynamicListWrapper from '@/components/dynamic-list-wrapper/DynamicListWrapper.vue'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import StatusPicker from '@/components/status-picker'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'

export default {
  name: 'FormPageBlogPost',
  validationRules,
  tabs,
  mediaTypesByKeys: mediaTypesByKeys(),
  availableWidgets: commonWidgetList,
  dynamicListName: 'widgets',
  components: {
    PageContent,
    InputTitleSlug,
    TranslationIcon,
    ManageSidePanel,
    MediaFilePicker,
    SeoSettings,
    DynamicListWrapper,
    FormHeader,
    StatusPicker,
    ProgressCircular
  },
  extends: FormAbstract,
  mixins: [
    widgetParentMix,
    parentDynamicList,
    validationTranslations,
    prepareTranslations,
    mixForm
  ],
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    blogCategories: {
      type: Array,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    language: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      activeTab: 0,
      fields: {
        title: null,
        slug: undefined,
        isFeatured: false,
        isRecommended: false,
        announcement: null,
        widgets: [],
        coverImageId: null,
        thumbnailId: null,
        seoMetaTitle: null,
        seoMetaDescription: null,
        seoDisallowIndex: false,
        seoMetaKeywords: null,
        seoRelCanonical: null,
        seoOgImageId: null,
        status: null,
        blogCategoryId: null,
        translations: { ru: { isEnabled: true } },
        sortOrder: null
      }
    }
  },
  computed: {
    redirerectToWebPortal () {
      if (this.data) {
        const { slug } = this.data
        const blogCategoryId = this.data?.blogCategoryId || null
        return window.location.href.includes('demo')
          ? `${process.env.VUE_APP_WEB_PORTAL_URL_DEMO}/blog/${blogCategoryId}/${slug}`
          : `${process.env.VUE_APP_WEB_PORTAL_URL}/blog/${blogCategoryId}/${slug}`
      }
      return '#'
    },
    getTitle () {
      if (this.data) {
        return this.data?.title || ''
      }
      return null
    },
    validationRulesVars () {
      return { id: this.data?.id, ctx: this, isNew: this.isNew }
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.coverImageId = data?.coverImage?.id || null
      this.fields.thumbnailId = data?.thumbnail?.id || null
      this.fields.widgets = Widget.populateList(data.widgets)
    }
  },
  methods: {
    getFieldErrorMessage (fieldName, msg = 'Обязательное поле') {
      if (this.$v.fields[fieldName] === undefined) return ''
      const v = this.$v.fields[fieldName]
      return v.$invalid && v.$dirty ? [msg] : []
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      this.mixPrepareTranslations(payload)
      this.$emit('submit', {
        data: payload
      })
    }
  }
}
</script>
